import { routes } from "@/constants/url";
import { supabase } from "@/lib/supabase";
import { Loading } from "@backpac/monica";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { useSearchParams } from "react-router";
import { Navigate } from "react-router";
import { z } from "zod";

// NOTE: http://localhost:5173/join-workspace/callback?targetEmail=test@test.com&workspaceId=test
export function JoinWorkspaceCallback() {
  return (
    <Suspense fallback={<Loading.FullHeight />}>
      <SearchParamsBoundary>{({ token, email }) => <CheckUser token={token} email={email} />}</SearchParamsBoundary>
    </Suspense>
  );
}

function CheckUser({ token, email }: { token: string; email: string }) {
  const { data } = useSuspenseQuery({
    queryKey: ["session"],
    queryFn: () => supabase.auth.getSession().then((res) => res.data),
  });

  const session = data.session;

  if (session?.user?.email !== email) {
    // NOTE: we trigger signout manually, because conclude that the user logged in with wrong email.
    supabase.auth.signOut();

    return (
      <Navigate
        to={{
          pathname: routes.JOIN_WORKSPACE.root,
          search: `token=${encodeURIComponent(token)}&joinWorkspaceError=${encodeURIComponent(
            `The email address (${session?.user?.email}) has not been invited to this workspace. Please try another email.`,
          )}`,
        }}
        replace
      />
    );
  }

  return <Navigate to={routes.WORKSPACE_NAVIGATOR.root} />;
}

const JoinWorkspaceCallbackSearchParamsSchema = z.object({
  token: z.string(),
  email: z.string().email(),
});

function SearchParamsBoundary({
  children,
}: { children: (params: z.infer<typeof JoinWorkspaceCallbackSearchParamsSchema>) => React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const { success, data } = JoinWorkspaceCallbackSearchParamsSchema.safeParse(Object.fromEntries(searchParams));

  if (success === false) {
    return (
      <Navigate
        to={{
          pathname: routes.AUTH.error,
          search: "message=BAD_ACCESS",
        }}
        replace
      />
    );
  }

  return children(data);
}
